import type { ReactNode } from 'react';
import { cx } from '../lib/cx';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/Icon';

export type BannerTone = 'info' | 'success' | 'warning' | 'danger';

const toneIcon: Record<BannerTone, IconName> = {
  info: 'info',
  success: 'check-circle',
  warning: 'alert-triangle',
  danger: 'alert-circle',
};

export interface BannerProps {
  tone?: BannerTone;
  title?: ReactNode;
  children: ReactNode;
  /** Trailing actions (e.g. a Retry button). */
  actions?: ReactNode;
  /** Show a dismiss ✕ and call this. */
  onClose?: () => void;
  /** Override the default per-tone icon; pass null to hide it. */
  icon?: ReactNode | null;
  /** Full-bleed strip: square corners, no side borders (app-bar banner). */
  flush?: boolean;
  className?: string;
  /** Accessible name for the dismiss ✕. English default. */
  dismissLabel?: string;
}

/** Inline banner for status / errors. Danger uses role="alert" (brief §4.2). */
export function Banner({
  tone = 'info',
  title,
  children,
  actions,
  onClose,
  icon,
  flush,
  className,
  dismissLabel = 'Dismiss',
}: BannerProps) {
  return (
    <div
      className={cx('tc-banner', `tc-banner--${tone}`, flush && 'tc-banner--flush', className)}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      {icon !== null && (
        <span className="tc-banner__icon" aria-hidden>
          {icon ?? <Icon name={toneIcon[tone]} size={20} />}
        </span>
      )}
      <div className="tc-banner__body">
        {title && <div className="tc-banner__title">{title}</div>}
        {children}
        {actions && <div className="tc-banner__actions">{actions}</div>}
      </div>
      {onClose && (
        <button type="button" className="tc-banner__close" aria-label={dismissLabel} onClick={onClose}>
          <Icon name="x" size={18} />
        </button>
      )}
    </div>
  );
}
